import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { FaArrowRight, FaPaperPlane } from 'react-icons/fa';
import { db } from '../../firebase/config';
import SEO from '../../components/SEO';
import ApplicationWizard from './ApplicationWizard';
import JobMeta from './JobMeta';
import { jobTitle } from './careersOptions';
import { descriptionText } from './richText';
import './Careers.css';

// Same icons as the values block of the About page
const VALUES = [
  { key: 'innovation', icon: '/assets/images/Ai-Technology-Spark Streamline Core-Neon.svg' },
  { key: 'partnership', icon: '/assets/images/Business-Handshake Streamline Core-Neon.svg' },
  { key: 'excellence', icon: '/assets/images/Star-Badge Streamline Core-Neon.svg' },
  { key: 'agility', icon: '/assets/images/Startup-Light Streamline Core-Neon.svg' }
];

const excerpt = (job, max = 220) => {
  const flat = descriptionText(job);
  return flat.length > max ? `${flat.slice(0, max).replace(/\s+\S*$/, '')}…` : flat;
};

const Careers = () => {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language !== 'en';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        // Rules only allow reading published offers, so the query must filter on it
        const snap = await getDocs(query(collection(db, 'jobs'), where('published', '==', true)));
        setJobs(snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0)));
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  return (
    <div className="careers-page">
      <SEO
        title={isFr ? 'Carrières - Rejoignez OPTEAM' : 'Careers - Join OPTEAM'}
        description={isFr
          ? 'Rejoignez OPTEAM Services : nos offres d\'emploi en RPA, IA et Low-Code, et candidature spontanée. Construisons ensemble l\'automatisation de demain.'
          : 'Join OPTEAM Services: our RPA, AI and Low-Code job openings, and spontaneous applications. Let\'s build the future of automation together.'
        }
        canonical="/careers"
      />

      <section className="careers-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>{t('careers.title')}</h1>
            <p>{t('careers.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <section className="careers-values">
        <div className="container">
          <h2 className="section-title">{t('careers.valuesTitle')}</h2>
          <div className="values-grid">
            {VALUES.map(v => (
              <div key={v.key} className="value-card">
                <div className="value-icon">
                  <img src={v.icon} alt="" className="value-icon-img" />
                </div>
                <h3>{t(`about.values.${v.key}.title`)}</h3>
                <p>{t(`about.values.${v.key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="careers-jobs">
        <div className="container">
          <h2 className="section-title">{t('careers.jobsTitle')}</h2>
          {loading ? (
            <p className="jobs-empty">{t('careers.loading')}</p>
          ) : jobs.length === 0 ? (
            <p className="jobs-empty">
              {t('careers.noJobs')} <a href="#spontaneous">{t('careers.noJobsLink')}</a>
            </p>
          ) : (
            <div className="jobs-list">
              {jobs.map(job => (
                <article key={job.id} className="job-card">
                  <h3><Link to={`/careers/${job.id}`}>{jobTitle(job)}</Link></h3>
                  <JobMeta job={job} />
                  {job.description && <p className="job-summary">{excerpt(job)}</p>}
                  <Link to={`/careers/${job.id}`} className="btn btn-primary">
                    {t('careers.viewAndApply')} <FaArrowRight />
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="careers-spontaneous" id="spontaneous">
        <div className="container spontaneous-inner">
          <h2>{t('careers.spontaneous.title')}</h2>
          <p>{t('careers.spontaneous.text')}</p>
          <button type="button" className="btn btn-primary" onClick={() => setApplying(true)}>
            <FaPaperPlane /> {t('careers.spontaneousCta')}
          </button>
        </div>
      </section>

      {applying && <ApplicationWizard onClose={() => setApplying(false)} />}
    </div>
  );
};

export default Careers;
